/**
 * 中国 SVG 地图。
 *
 * 移植自原型 `renderMap` / `geoPath`：
 *   ① 只画省级行政区（adcode 以 0000 结尾），其余（如南海诸岛）作 nodata
 *   ② 每个省一个 <path>
 *   ③ 省名 + 健康度数字（居中，取自 GeoJSON properties.center）
 *   ④ 项目光点 <circle>
 *
 * ⚠️ React 数据驱动渲染，绝不用 innerHTML 命令式操作 DOM。
 */

import { memo, useMemo } from 'react';
import {
  createProjection,
  geometryToPath,
  isProvinceFeature,
  project,
  shortProvinceName,
  type ChinaGeo,
} from './projection';
import { levelColor } from './colors';
import ProjectDot from './ProjectDot';
import type { MapProject, ProvinceAgg } from './useMapData';
import type { MouseEvent as ReactMouseEvent } from 'react';

export interface MapTransform {
  x: number;
  y: number;
  k: number;
}

export interface ChinaMapProps {
  width: number;
  height: number;
  geo: ChinaGeo | undefined;
  projects: readonly MapProject[];
  provinces: readonly ProvinceAgg[];
  transform: MapTransform;
  selectedProvinceCode?: string;
  /** ⭐ P6.54 搜索命中集合；非 null 时未命中的光点淡化 */
  searchMatchIds?: ReadonlySet<string> | null;
  /** ⭐ P6.54 被搜索定位的项目（加聚焦环） */
  highlightProjectId?: string | null;
  onProvinceClick: (provinceCode: string) => void;
  onDotEnter: (project: MapProject, e: ReactMouseEvent) => void;
  onDotLeave: () => void;
  onDotClick: (project: MapProject) => void;
}

function ChinaMap({
  width,
  height,
  geo,
  projects,
  provinces,
  transform,
  selectedProvinceCode,
  searchMatchIds,
  highlightProjectId,
  onProvinceClick,
  onDotEnter,
  onDotLeave,
  onDotClick,
}: ChinaMapProps) {
  const projection = useMemo(() => createProjection(width, height), [width, height]);

  /** 省份轮廓（几何点量大，必须 memo，避免 hover 时重算） */
  const provincePaths = useMemo(() => {
    if (!geo) return [];
    return geo.features.map((f) => ({
      key: String(f.properties.adcode ?? f.properties.name),
      name: f.properties.name,
      code: String(f.properties.adcode ?? ''),
      isProvince: isProvinceFeature(f),
      d: geometryToPath(projection, f.geometry),
    }));
  }, [geo, projection]);

  const provinceByCode = useMemo(() => {
    const m = new Map<string, ProvinceAgg>();
    for (const p of provinces) m.set(p.provinceCode, p);
    return m;
  }, [provinces]);

  /**
   * 省名标签。
   *
   * ⚠️ 必须遍历 **GeoJSON 全部省份**（34 个），不能只用有项目的省（12 个）。
   *    设计稿是全国地图，每个省份都要标名；有项目的省额外显示健康度数字。
   *    （此前只用 stats.byProvince，导致北京、内蒙古、四川等省名不显示。）
   */
  const labels = useMemo(() => {
    if (!geo) return [];
    return geo.features
      .filter((f) => isProvinceFeature(f) && (f.properties.center ?? f.properties.centroid))
      .map((f) => {
        const code = String(f.properties.adcode ?? '');
        const center = (f.properties.center ?? f.properties.centroid) as [number, number];
        const [x, y] = project(projection, center[0], center[1]);
        return {
          key: code || String(f.properties.name),
          x,
          y,
          name: String(f.properties.name),
          agg: provinceByCode.get(code),
        };
      });
  }, [geo, provinceByCode, projection]);

  const dots = useMemo(
    () =>
      projects.map((p) => {
        const [x, y] = project(projection, p.longitude, p.latitude);
        return { p, x, y };
      }),
    [projects, projection],
  );

  return (
    <svg className="map-svg" width={width} height={height} role="img" aria-label="全国项目态势地图">
      <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
        {/* ① 省份轮廓 */}
        <g className="map-prov-layer">
          {provincePaths.map((prov) => {
            const hasData = prov.isProvince && provinceByCode.has(prov.code);
            const selected = hasData && prov.code === selectedProvinceCode;
            const cls = ['prov', hasData ? '' : 'nodata', selected ? 'hl' : '']
              .filter(Boolean)
              .join(' ');
            return (
              <path
                key={prov.key}
                d={prov.d}
                className={cls}
                onClick={
                  hasData
                    ? (e) => {
                        e.stopPropagation();
                        onProvinceClick(prov.code);
                      }
                    : undefined
                }
              />
            );
          })}
        </g>

        {/* ② 省名（全部省份）+ 健康度数字（仅有的省份） */}
        <g className="map-label-layer">
          {labels.map((l) => {
            const showHealth =
              l.agg && l.agg.level !== 'IDLE' && l.agg.level !== 'FROZEN';
            return (
              <g key={l.key}>
                <text
                  className={l.agg ? 'plabel plabel--has' : 'plabel'}
                  x={l.x}
                  y={l.y}
                >
                  {shortProvinceName(l.name)}
                </text>
                {showHealth && (
                  <text
                    className="plabel plabel__v"
                    x={l.x}
                    y={l.y + 12}
                    style={{ fill: levelColor(l.agg!.level) }}
                  >
                    {l.agg!.avgHealth.toFixed(0)}
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* ③ 项目光点 */}
        <g className="map-dot-layer">
          {dots.map(({ p, x, y }) => (
            <ProjectDot
              key={p.id}
              project={p}
              x={x}
              y={y}
              dimmed={searchMatchIds ? !searchMatchIds.has(p.id) : false}
              focused={highlightProjectId === p.id}
              onEnter={onDotEnter}
              onLeave={onDotLeave}
              onClick={onDotClick}
            />
          ))}
        </g>
      </g>
    </svg>
  );
}

export default memo(ChinaMap);
